import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SELECT_VENUE_ROUTE } from '@constants/routes';
import { useAppLaunchDetection } from '@hooks/use-app-launch-detection';
import { getLastPathCookie } from '@utils/last-path-storage/get-last-path-cookie';

/**
 * Component that handles last path restoration on app launch for Xceed Pro webview
 * It will redirect to the last visited path when the app is launched
 * Uses localStorage for persistence instead of cookies for better reliability
 */
export const LastPathPersistence: React.FC = () => {
  const { isInitialLoad, resetInitialLoad, startRestoration, endRestoration } = useAppLaunchDetection();
  const location = useLocation();

  useLayoutEffect(() => {
    // Only apply during initial load (any path, not just root paths)
    if (isInitialLoad) {
      // Add a small delay to ensure any pending localStorage operations are complete
      setTimeout(() => {
        // Get the last visited path from localStorage
        const lastPath = getLastPathCookie();

        // Reset the initial load state to prevent multiple redirects
        resetInitialLoad();

        // Decide on the pathname but replace() with the full stored value: comparing the full path
        // loops when a route normalizes its query on mount, and it also drops a launch-time query on a
        // deep link. Matching pathnames after the reload guarantees termination whatever the query does.
        const storedPathname = lastPath?.split('?')[0];
        if (lastPath && storedPathname !== location.pathname && lastPath !== '/' && lastPath !== SELECT_VENUE_ROUTE) {
          // Start restoration to prevent navigation context from saving the path
          startRestoration();

          // Use replace to completely bypass the router
          // This prevents any router redirects from happening
          globalThis.location.replace(lastPath);

          // Note: endRestoration won't be called because the page will reload
        } else {
          // End restoration since we're not navigating
          setTimeout(() => {
            endRestoration();
          }, 100);
        }
      }, 50); // Small delay to ensure localStorage operations are complete
    }
  }, [isInitialLoad, location.pathname, location.search, resetInitialLoad, startRestoration, endRestoration]);

  // This component doesn't render anything
  return null;
};
